/**
 * Showlist Common Utilities
 * Shared functions used across multiple page scripts
 */

/**
 * Safely retrieve the global Firebase object.
 *
 * Accessing `firebase` directly will throw a ReferenceError when the Firebase SDK
 * script fails to load (for example, when an upstream cache serves a truncated file).
 * We always use `window.firebase` behind a guard to avoid creating additional runtime
 * errors that mask the original root cause.
 *
 * @returns {Object|null} Firebase global object or null when unavailable
 */
export function getFirebaseGlobal() {
  if (typeof window === 'undefined') {
    return null;
  }

  return window.firebase || null;
}

/**
 * Check whether Firebase Auth is available and callable.
 *
 * Some failure modes leave a partially loaded Firebase object where `auth` is
 * missing or not a function. This helper centralizes that defensive check so page
 * modules can bail out early and keep the rest of the UI functional.
 *
 * @returns {boolean} True when Firebase Auth can be used safely
 */
export function hasFirebaseAuth() {
  const firebaseGlobal = getFirebaseGlobal();
  return !!(firebaseGlobal && typeof firebaseGlobal.auth === 'function');
}

/**
 * Initialize Firebase app with configuration from WordPress
 * Safe to call multiple times - will only initialize once
 * @param {string} logPrefix - Prefix for log messages (e.g., 'theme.js')
 * @returns {boolean} True when Firebase app is initialized and ready, false otherwise
 */
export function initializeFirebase(logPrefix = 'utils') {
  try {
    const firebaseGlobal = getFirebaseGlobal();

    if (!firebaseGlobal) {
      console.warn(`${logPrefix}: Firebase SDK unavailable. Skipping Firebase initialization.`);
      return false;
    }

    if (!firebaseGlobal.apps || firebaseGlobal.apps.length === 0) {
      firebaseGlobal.initializeApp(site.firebaseConfig);
    }

    return true;
  } catch (error) {
    // App might already be initialized, which is fine.
    // We still return false so callers can decide whether to continue Firebase-only flows.
    console.log(`${logPrefix}: Firebase app already initialized or error:`, error);
    return false;
  }
}

/**
 * Check if the current URL is a Firebase email sign-in link
 * This detects when user is returning from clicking a sign-in link in their email
 * @returns {boolean} True if URL contains Firebase email link parameters
 */
export function isEmailSignInLink() {
  // Firebase email links contain specific query parameters
  // Check if Firebase auth is available and if URL matches email link pattern
  try {
    const firebaseGlobal = getFirebaseGlobal();
    if (firebaseGlobal && typeof firebaseGlobal.auth === 'function') {
      return firebaseGlobal.auth().isSignInWithEmailLink(window.location.href);
    }
  } catch (error) {
    // Firebase might not be initialized yet, which is fine
  }
  return false;
}

/**
 * Get the page loader DOM element
 * @returns {HTMLElement|null} The page loader element or null if not found
 */
function getPageLoaderElement() {
  return document.getElementById('page-loader');
}

/**
 * Show the page loader directly
 * Simple utility function for one-off show operations
 * For repeated show/hide operations, use createPageLoaderManager() instead
 */
export function showPageLoader() {
  const loader = getPageLoaderElement();
  if (loader) {
    loader.style.display = 'flex';
  }
}

/**
 * Hide the page loader directly
 * Simple utility function for one-off hide operations
 * For repeated show/hide operations, use createPageLoaderManager() instead
 */
export function hidePageLoader() {
  const loader = getPageLoaderElement();
  if (loader) {
    loader.style.display = 'none';
  }
}

/**
 * Create a page loader manager with single-hide guarantee
 * Uses a flag to ensure the loader is only hidden once
 * This prevents race conditions where multiple event handlers try to hide it
 * Directly manipulates the DOM element instead of relying on window.pageLoader
 * @returns {Object} Object with hide() and show() methods
 */
export function createPageLoaderManager() {
  let hidden = false;
  return {
    hide() {
      if (hidden) return;
      hidden = true;
      const loader = getPageLoaderElement();
      if (loader) {
        loader.style.display = 'none';
      }
    },
    show() {
      // Reset the hidden flag when showing, so it can be hidden again later
      hidden = false;
      const loader = getPageLoaderElement();
      if (loader) {
        loader.style.display = 'flex';
      }
    },
    isHidden() {
      return hidden;
    }
  };
}

/**
 * Remove specified query parameters from the URL without page reload
 * @param {string[]} paramsToRemove - Array of parameter names to remove
 */
export function cleanUrlParams(paramsToRemove) {
  const url = new URL(window.location.href);
  paramsToRemove.forEach(p => url.searchParams.delete(p));
  window.history.replaceState({}, document.title, url.toString());
}

/**
 * Get URL search parameters as an object
 * @returns {URLSearchParams}
 */
export function getUrlParams() {
  return new URLSearchParams(window.location.search);
}

